import { useEffect, useRef, useState } from "react";

import {
  createWorkspaceInvitation,
  listWorkspaceInvitations,
  retryWorkspaceInvitation,
  revokeWorkspaceInvitation,
  type WorkspaceInvitation,
} from "../../lib/account-client";

export interface TeamInvitationsApi {
  listWorkspaceInvitations: typeof listWorkspaceInvitations;
  createWorkspaceInvitation: typeof createWorkspaceInvitation;
  retryWorkspaceInvitation: typeof retryWorkspaceInvitation;
  revokeWorkspaceInvitation: typeof revokeWorkspaceInvitation;
}

export function TeamInvitations({ workspaceId, api, embedded = false }: { workspaceId: string; api: TeamInvitationsApi; embedded?: boolean }) {
  const [items, setItems] = useState<WorkspaceInvitation[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"creator" | "viewer">("creator");
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<"queued" | "manual" | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyInvitationId, setBusyInvitationId] = useState<string | null>(null);
  const snapshotGeneration = useRef(0);

  useEffect(() => {
    let active = true;
    const generation = ++snapshotGeneration.current;
    void api.listWorkspaceInvitations(workspaceId)
      .then((invitations) => {
        if (active && generation === snapshotGeneration.current) setItems(invitations);
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [api, workspaceId]);

  useEffect(() => {
    if (!items.some((item) => item.status === "pending" && ["pending", "sending", "retrying"].includes(item.deliveryStatus))) return;
    let active = true;
    const timer = window.setTimeout(() => {
      const generation = snapshotGeneration.current;
      void api.listWorkspaceInvitations(workspaceId)
        .then((invitations) => {
          if (active && generation === snapshotGeneration.current) setItems(invitations);
        })
        .catch(() => undefined);
    }, 3_000);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [api, items, workspaceId]);

  const create = async () => {
    snapshotGeneration.current += 1;
    setSaving(true);
    setError(null);
    setInviteUrl(null);
    setDelivery(null);
    setCopied(false);
    try {
      const result = await api.createWorkspaceInvitation(workspaceId, { email, role });
      setItems((current) => [result.invitation, ...current]);
      setInviteUrl(result.inviteUrl);
      setDelivery(result.delivery);
      setEmail("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Doorway could not create this invitation.");
    } finally {
      setSaving(false);
    }
  };

  const copyInvite = async () => {
    if (!inviteUrl) return;
    setError(null);
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
    } catch {
      setError("Doorway could not copy the invitation link. Select the URL and copy it manually.");
    }
  };

  const retry = async (item: WorkspaceInvitation) => {
    snapshotGeneration.current += 1;
    setBusyInvitationId(item.id);
    setError(null);
    try {
      await api.retryWorkspaceInvitation(workspaceId, item.id);
      setItems((current) => current.map((candidate) => {
        if (candidate.id !== item.id) return candidate;
        const { deliveryError: _deliveryError, ...invitation } = candidate;
        return { ...invitation, deliveryStatus: "retrying" };
      }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Doorway could not retry this invitation email.");
    } finally {
      setBusyInvitationId(null);
    }
  };

  const revoke = async (item: WorkspaceInvitation) => {
    snapshotGeneration.current += 1;
    setBusyInvitationId(item.id);
    setError(null);
    try {
      await api.revokeWorkspaceInvitation(workspaceId, item.id);
      setItems((current) => current.map((candidate) => candidate.id === item.id
        ? { ...candidate, status: "revoked" }
        : candidate));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Doorway could not revoke this invitation.");
    } finally {
      setBusyInvitationId(null);
    }
  };

  const content = (
    <>
      <div className="workspace-section-heading"><div><p className="step-label">Team access</p><h3 id="invitations-heading">Invite someone</h3></div><span className="access-state access-active">Available</span></div>
      <p className="workspace-section-copy">We will email a private invitation to the exact Google account you enter. Invitations expire after seven days.</p>
      <form onSubmit={(event) => { event.preventDefault(); void create(); }} className="invitation-form">
        <label><span>Email</span><input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} disabled={saving} /></label>
        <label><span>Role</span><select value={role} onChange={(event) => setRole(event.target.value === "viewer" ? "viewer" : "creator")} disabled={saving}><option value="creator">Creator</option><option value="viewer">Viewer</option></select></label>
        <button className="primary-button" type="submit" disabled={saving}>{saving ? "Sending..." : "Send invitation"}</button>
      </form>
      {error && <p className="error-message" role="alert">{error}</p>}
      {inviteUrl && <div className="invite-link" role="status"><span>{delivery === "queued" ? "Invitation email queued. You can also share this link directly." : "Email delivery is unavailable. Share this link directly."}</span><div className="invite-link-actions"><a href={inviteUrl}>{inviteUrl}</a><button type="button" className="text-button" aria-label={copied ? "Invitation link copied" : "Copy invitation link"} onClick={() => void copyInvite()}>{copied ? "Copied" : "Copy link"}</button></div></div>}
      {items.length > 0 && (
        <ul className="invitation-list">
          {items.map((item) => {
            const busy = busyInvitationId === item.id;
            return (
              <li key={item.id}>
                <span>
                  <strong>{item.email}</strong>
                  <small>{item.role} · <span>{invitationStateLabel(item)}</span></small>
                  {item.status === "pending" && item.deliveryStatus === "failed" && (
                    <small>{item.deliveryError?.message ?? "Email delivery failed. Try again."}</small>
                  )}
                </span>
                {item.status === "pending" && item.deliveryStatus === "failed" && (
                  <button type="button" className="text-button" disabled={busy} onClick={() => void retry(item)}>
                    {busy ? "Retrying..." : "Retry email"}
                  </button>
                )}
                {item.status === "pending" && (
                  <button type="button" className="text-button" disabled={busy} onClick={() => void revoke(item)}>Revoke</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
  return embedded
    ? <div className="team-invitations team-invitations-embedded" aria-labelledby="invitations-heading">{content}</div>
    : <section className="workspace-section team-invitations" aria-labelledby="invitations-heading">{content}</section>;
}

function invitationStateLabel(invitation: WorkspaceInvitation): string {
  if (invitation.status === "revoked") return "Revoked";
  if (invitation.status === "accepted") return "Accepted";
  if (invitation.status === "expired") return "Expired";
  switch (invitation.deliveryStatus) {
    case "sent": return "Sent";
    case "failed": return "Failed";
    case "retrying": return "Retrying";
    case "manual": return "Email unavailable";
    default: return "Pending";
  }
}
